import {
  TRACKER_GOING_STATES,
  type TrackerBookingSource,
  type TrackerGoingState,
  type TrackerGuest,
} from '../services/tracker-api-client';

export interface TrackerRow {
  guest: TrackerGuest;
  /** 0 for an invitee, 1 for someone brought as a plus-one. */
  depth: number;
  /** The inviter's name, when the plus-one's host is on this roster. */
  plusOneOf: string | null;
}

export function guestGoingState(guest: Pick<TrackerGuest, 'going'>): TrackerGoingState {
  const value = (guest.going || '').trim().toLowerCase();
  return (TRACKER_GOING_STATES as readonly string[]).includes(value)
    ? (value as TrackerGoingState)
    : 'invited';
}

export function guestBookingSource(
  guest: Pick<TrackerGuest, 'bookingSource'>,
): TrackerBookingSource | null {
  const value = (guest.bookingSource || '').trim().toLowerCase();
  return value === 'tripsby' || value === 'guest' ? value : null;
}

/**
 * Roster order: each invitee followed by the people they brought.
 *
 * A plus-one whose inviter is missing from the roster stays at the top level
 * rather than disappearing — the tracker must never hide a guest because a
 * link is broken.
 */
export function orderGuestsWithPlusOnes(guests: readonly TrackerGuest[]): TrackerRow[] {
  const byId = new Map(guests.map((guest) => [guest.inviteeId, guest]));
  const children = new Map<string, TrackerGuest[]>();
  const roots: TrackerGuest[] = [];

  for (const guest of guests) {
    const inviterId = guest.plusOneOfInviteeId;
    if (inviterId && inviterId !== guest.inviteeId && byId.has(inviterId)) {
      const siblings = children.get(inviterId) ?? [];
      siblings.push(guest);
      children.set(inviterId, siblings);
    } else {
      roots.push(guest);
    }
  }

  const rows: TrackerRow[] = [];
  for (const guest of roots) {
    rows.push({ guest, depth: 0, plusOneOf: null });
    for (const child of children.get(guest.inviteeId) ?? []) {
      rows.push({ guest: child, depth: 1, plusOneOf: guest.fullName });
    }
  }
  return rows;
}

/** Hotel names already in use, for the "Staying at" combobox. */
export function distinctStayingAt(guests: readonly TrackerGuest[]): string[] {
  const seen = new Map<string, string>();
  for (const guest of guests) {
    const value = guest.stayingAt?.trim();
    if (!value) continue;
    const key = value.toLowerCase();
    if (!seen.has(key)) seen.set(key, value);
  }
  return [...seen.values()].sort((a, b) => a.localeCompare(b));
}

function relativeTime(value: Date): string {
  const minutes = Math.round((Date.now() - value.getTime()) / 60000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  return new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric' }).format(value);
}

/**
 * "Edited by jonathan · 12m ago" — the collaboration signal that replaces
 * presence avatars. Only the local part of the address is shown: the row is
 * already crowded and the domain adds nothing.
 */
export function formatEditedBy(
  updatedBy?: string | null,
  updatedAt?: string | null,
): string | null {
  const editor = updatedBy?.trim();
  if (!editor && !updatedAt) return null;

  const name = editor ? editor.split('@')[0] : null;
  const stamp = updatedAt ? new Date(updatedAt) : null;
  const when = stamp && !Number.isNaN(stamp.getTime()) ? relativeTime(stamp) : null;

  if (name && when) return `Edited by ${name} · ${when}`;
  if (name) return `Edited by ${name}`;
  return when ? `Edited ${when}` : null;
}

/** `<input type="date">` wants YYYY-MM-DD; the API may answer a full instant. */
export function toDateInputValue(value?: string | null): string {
  const trimmed = value?.trim();
  if (!trimmed) return '';
  if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) return trimmed;
  const parsed = new Date(trimmed);
  if (Number.isNaN(parsed.getTime())) return '';
  return parsed.toISOString().slice(0, 10);
}
